import { jsonRequestInit } from './jsonRequest';
import { getAccessToken, isTokenExpired } from '../auth';
import { viewAsUserId } from './viewAs';

// Mirrored by backend/app/services/reader_telemetry.py and pinned by a parity test.
export const READER_EVENT_TYPES = ['visit', 'search_started', 'search', 'feed_add', 'login_gate_hit', 'story_hide', 'story_hide_not_about_feed', 'story_hide_less_from_domain', 'story_hide_low_quality', 'story_hide_already_seen', 'story_hide_other', 'story_hide_no_reason', 'story_unhide', 'feed_home_toggle', 'feed_delete', 'feed_restore', 'feed_served', 'story_link_copy', 'story_feed_click', 'auth_dialog'] as const;
export type ReaderEvent = typeof READER_EVENT_TYPES[number];
export type SearchMethod = 'search_box' | 'recent_search' | 'window_toggle' | 'feed_edit' | 'retry' | 'url';
const ANON_KEY = 'reader_anon_id';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let memoryId: string | undefined;

export function readerAnonId(): string {
  if (memoryId) return memoryId;
  try {
    const stored = localStorage.getItem(ANON_KEY);
    if (stored && UUID_PATTERN.test(stored)) return (memoryId = stored);
  } catch { /* Storage can be disabled; keep a stable ID for this document. */ }
  memoryId = crypto.randomUUID();
  try { localStorage.setItem(ANON_KEY, memoryId); } catch { /* use the in-memory ID */ }
  return memoryId;
}

/** Telemetry never refreshes credentials, so a stale token means "anonymous" rather than a 401 that drops the event. */
export function telemetryAccessToken(): string | null {
  try {
    const token = getAccessToken();
    return token && !isTokenExpired(token) ? token : null;
  } catch { return null; }
}

/** A telemetry failure must never refresh credentials, log out, or affect a gesture. */
export function sendTelemetry(path: string, payload: Record<string, unknown>): void {
  // An admin viewing as another reader is not that reader's engagement, nor their own (`utils/viewAs`).
  if (viewAsUserId()) return;
  try {
    const init = jsonRequestInit('POST', { ...payload, anon_id: readerAnonId() });
    const body = init.body as string;
    if (new TextEncoder().encode(body).length > 60 * 1024) return;
    const headers = new Headers(init.headers);
    const token = telemetryAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    void fetch(path, { ...init, headers, keepalive: true }).catch(() => {});
  } catch { /* Includes unavailable browser storage and synchronous fetch errors. */ }
}

export function readerEvent(event: ReaderEvent, properties: Record<string, unknown>): void {
  sendTelemetry('/api/v1/reader/events', { event, properties });
}

export interface SearchAttempt {
  id: string;
  method: SearchMethod;
  query: string;
  windowHours: 24 | 72;
  descriptionId?: string | null;
  completed: boolean;
}
let handoff: SearchAttempt | null = null;

export function startSearchAttempt(method: SearchMethod, query: string, windowHours: 24 | 72, descriptionId?: string | null): SearchAttempt {
  const attempt: SearchAttempt = { id: crypto.randomUUID(), method, query: query.trim(), windowHours, descriptionId, completed: false };
  readerEvent('search_started', searchProperties(attempt));
  return attempt;
}

export function searchProperties(attempt: SearchAttempt): Record<string, unknown> {
  return { search_attempt_id: attempt.id, method: attempt.method, query: attempt.query,
    window_hours: attempt.windowHours, description_id: attempt.descriptionId ?? null };
}

export function completeSearchAttempt(attempt: SearchAttempt | null, runId: string | null, count: number | null, query?: string): void {
  if (!attempt || attempt.completed) return;
  attempt.completed = true;
  readerEvent('search', { ...searchProperties(attempt), query: attempt.query || query?.trim() || '',
    run_id: runId, result_count: count, outcome: count === null ? 'failed' : 'completed' });
}

/** In-memory handoff survives SPA navigation; reload/back must create a URL attempt. */
export function handOffSearchAttempt(attempt: SearchAttempt): Record<string, string> {
  handoff = attempt;
  return { readerAttemptId: attempt.id };
}

export function takeSearchAttempt(id: unknown): SearchAttempt | null {
  if (!handoff || handoff.id !== id) return null;
  const attempt = handoff;
  handoff = null;
  return attempt;
}

export interface ReaderView { key: string; view_type: 'default' | 'for_you' | 'search' | 'feed' | 'auth'; search_id?: string; description_id?: string }
export function readerView(pathname: string, search: string, loggedIn: boolean): ReaderView | null {
  if (['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'].includes(pathname)) return { key: pathname, view_type: 'auth' };
  const feed = pathname.match(/^\/feeds\/([^/]+)\/?$/);
  if (feed) {
    try { return { key: `feed:${feed[1]}`, view_type: 'feed', search_id: decodeURIComponent(feed[1]) }; }
    catch { return null; }
  }
  const params = new URLSearchParams(search);
  // The feeds v2 search page; without a query it is the empty composer, not a search.
  if (pathname === '/search') return params.get('q')?.trim() ? { key: 'search', view_type: 'search' } : null;
  // A named search, `/search/<id>` (`/search/new` is the old composer's redirect, not a search).
  if (/^\/search\/(?!new\/?$)[^/]+\/?$/.test(pathname)) return { key: 'search', view_type: 'search' };
  if (pathname !== '/') return null;
  // A legacy interest link is a redirect, not a search view.
  if (params.has('interest') && !params.has('search') && !params.has('search_id') && !params.has('run')) return null;
  const descriptionId = params.get('search') || params.get('search_id');
  if (descriptionId || params.has('run')) return { key: 'search', view_type: 'search', ...(descriptionId ? { description_id: descriptionId } : {}) };
  if (params.has('edit')) return null;
  return { key: 'home', view_type: loggedIn ? 'for_you' : 'default' };
}

export function isVisit(previous: ReaderView | null, next: ReaderView | null, navigation: string): boolean {
  return !!next && (!previous || next.key !== previous.key || navigation === 'POP');
}
